import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [counters, setCounters] = useState([0, 0, 0, 0]);

  const stats = [
    { number: '25+', label: 'Years Experience', target: 25 },
    { number: '500+', label: 'Generators Serviced', target: 500 },
    { number: '50+', label: 'International Companies', target: 50 },
    { number: '24/7', label: 'Emergency Support', target: 24 }
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Trusted Expertise',
      description: 'Years of experience in industrial diesel generator solutions with certified technicians.'
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Response',
      description: 'Quick turnaround times for repairs and emergency services to minimize downtime.'
    },
    {
      icon: 'ri-award-line',
      title: 'Quality Guarantee',
      description: 'All our work comes with comprehensive warranties and quality assurance.'
    }
  ];

  // Counter animation effect
  useEffect(() => {
    if (isInView) {
      stats.forEach((stat, index) => {
        if (index === 3) return; // Skip 24/7 counter
        
        let start = 0;
        const end = stat.target;
        const duration = 2000;
        const increment = end / (duration / 16);
        
        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            start = end;
            clearInterval(timer);
          }
          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = Math.floor(start);
            return newCounters;
          });
        }, 16);
      });
    }
  }, [isInView]);

  // Cool container animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  // Enhanced feature card animations
  const featureVariants = {
    hidden: { 
      opacity: 0, 
      x: -60,
      rotateY: -15
    },
    visible: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  // Magnetic hover effect for feature cards
  const featureHoverVariants = {
    hover: {
      x: 10,
      scale: 1.02,
      rotateY: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    }
  };

  // Stats animation
  const statsVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.8
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  return (
    <section id="about" className="py-20 bg-white overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.h2 
              className="text-4xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: -30, rotateX: -15 }}
              animate={isInView ? { 
                opacity: 1, 
                y: 0,
                rotateX: 0,
                transition: {
                  type: "spring",
                  damping: 12,
                  stiffness: 100,
                  duration: 0.8
                }
              } : { opacity: 0, y: -30, rotateX: -15 }}
            >
              About ZAID ENTERPRISE
            </motion.h2>
            
            <motion.p 
              className="text-lg text-gray-600 mb-6 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { 
                opacity: 1, 
                y: 0,
                transition: { delay: 0.2, duration: 0.8 }
              } : { opacity: 0, y: 30 }}
            >
              With over 25 years of expertise in high-speed diesel and HFO (Heavy Fuel Oil) generator solutions, 
              ZAID ENTERPRISE has established itself as a trusted partner for industrial power generation needs. 
              We have successfully worked with international companies across various industries, providing 
              reliable generator services, maintenance, and sales worldwide.
            </motion.p>
            
            <motion.p 
              className="text-lg text-gray-600 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { 
                opacity: 1, 
                y: 0,
                transition: { delay: 0.4, duration: 0.8 }
              } : { opacity: 0, y: 30 }}
            >
              Our extensive experience with global clients has given us deep insights into diverse operational 
              requirements and international standards. From marine applications to industrial facilities, 
              we deliver high-quality used generators and comprehensive support services that meet the 
              demanding needs of international operations.
            </motion.p>
            
            <motion.div 
              className="space-y-6 mb-8"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {features.map((feature, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-start space-x-4 cursor-pointer"
                  variants={featureVariants}
                  whileHover={featureHoverVariants.hover}
                >
                  <motion.div 
                    className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: 10,
                      backgroundColor: "#3B82F6",
                      transition: { duration: 0.3 }
                    }}
                  >
                    <motion.i 
                      className={`${feature.icon} text-blue-600 text-xl`}
                      whileHover={{ 
                        color: "#FFFFFF",
                        scale: 1.2
                      }}
                    />
                  </motion.div>
                  <div>
                    <motion.h3 
                      className="text-xl font-semibold text-gray-900 mb-2"
                      whileHover={{ x: 5 }}
                    >
                      {feature.title}
                    </motion.h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
              variants={statsVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {stats.map((stat, index) => (
                <motion.div 
                  key={index} 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.5, rotateY: -90 }}
                  animate={isInView ? { 
                    opacity: 1, 
                    scale: 1,
                    rotateY: 0,
                    transition: {
                      delay: 0.6 + index * 0.1,
                      type: "spring",
                      damping: 15,
                      stiffness: 200
                    }
                  } : { opacity: 0, scale: 0.5, rotateY: -90 }}
                  whileHover={{
                    scale: 1.1,
                    y: -5,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 10
                    }
                  }}
                >
                  <motion.div 
                    className="text-3xl font-bold text-blue-600 mb-2"
                    animate={isInView && index !== 3 ? {
                      scale: [1, 1.1, 1],
                      transition: {
                        duration: 0.5,
                        delay: 1 + index * 0.2
                      }
                    } : {}}
                  >
                    {index === 3 ? stat.number : 
                     index === 0 ? `${counters[index]}+` :
                     index === 1 ? `${counters[index]}+` :
                     `${counters[index]}+`}
                  </motion.div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 100, rotateY: -15 }}
            animate={isInView ? { 
              opacity: 1, 
              x: 0,
              rotateY: 0,
              transition: {
                type: "spring",
                damping: 20,
                stiffness: 80,
                duration: 1
              }
            } : { opacity: 0, x: 100, rotateY: -15 }}
          >
            <motion.div
              className="relative overflow-hidden rounded-xl"
              whileHover={{
                scale: 1.02,
                rotateY: 5,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 20
                }
              }}
            >
              <motion.img 
                src="https://static.readdy.ai/image/ae15596087fd532e67992f2affe9d6ee/0876ad674ac6d64333b8d14dd560924f.jpeg"
                alt="Professional generator maintenance service"
                className="rounded-xl shadow-lg object-cover w-full h-[500px] object-top"
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              />
              
              {/* Floating elements around the image */}
              <motion.div 
                className="absolute top-10 -left-4 w-8 h-8 bg-blue-400 rounded-full opacity-60"
                animate={{
                  y: [0, -20, 0],
                  x: [0, 10, 0],
                  rotate: [0, 180, 360],
                  transition: {
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
              />
              <motion.div 
                className="absolute bottom-20 -right-6 w-6 h-6 bg-blue-300 rounded-full opacity-50"
                animate={{
                  y: [0, 15, 0],
                  x: [0, -15, 0],
                  rotate: [0, -180, -360],
                  transition: {
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }
                }}
              />
            </motion.div>
            
            <motion.div 
              className="absolute -bottom-6 -left-6 bg-orange-600 text-white p-6 rounded-lg shadow-lg"
              initial={{ opacity: 0, scale: 0.5, rotateZ: -15 }}
              animate={isInView ? { 
                opacity: 1, 
                scale: 1,
                rotateZ: 0,
                transition: {
                  delay: 0.8,
                  type: "spring",
                  damping: 15,
                  stiffness: 200
                }
              } : { opacity: 0, scale: 0.5, rotateZ: -15 }}
              whileHover={{
                scale: 1.05,
                rotateZ: 2,
                boxShadow: "0 20px 40px rgba(249, 115, 22, 0.3)",
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 10
                }
              }}
            >
              <motion.div 
                className="text-2xl font-bold"
                animate={{
                  scale: [1, 1.05, 1],
                  transition: {
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }}
              >
                Professional
              </motion.div>
              <div className="text-sm">Generator Services</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
